import { labelDraftContract } from "./label-drafts.contract";
import { oc } from "@orpc/contract";
import { SystemEntity, loadTransformSchema } from "@erp/shared-types";
import { z } from "zod";

// ============================================================================
// Scalar Schemas
// ============================================================================

const UUIDSchema = z.string().uuid();

export const SchemaFieldTypeSchema = z.enum([
  "string",
  "text",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
  "time",
  "datetime",
  "enum",
  "json",
  "range",
  "file",
  "image",
  "url",
  "geospatial",
  "currency",
  "unit",
  "object",
  "array",
  "entity_reference",
  "document",
]);

/** The entity an `entity_reference` field points at. Null on every other type. */
const ReferencedEntityTypeSchema = z.enum(SystemEntity).nullable().optional();

/**
 * Request-side schema type. `extension` is the retired name of an extraction
 * variant; it is still accepted, and read as `extraction`, so older callers and
 * task-type bundles exported before the rename keep working.
 */
export const SchemaTypeSchema = z
  .enum(["extraction", "extension", "sql"])
  .transform((value) => (value === "extension" ? "extraction" : value));
export const SchemaModeSchema = z.enum(["standalone", "extend_base"]);

// Index kinds a field can declare for its ingest-table column. Mirrors
// SCHEMA_FIELD_INDEX_TYPES in @erp/shared-types. Indexes are opt-in: null and
// an empty array both mean no indexes.
const SchemaFieldIndexTypeSchema = z.enum(["btree", "hash", "trigram", "gin"]);

const FieldIndexesSchema = z
  .array(SchemaFieldIndexTypeSchema)
  .nullable()
  .optional();

const ValidationRulesSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    pattern: z.string().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    precision: z.number().optional(),
    scale: z.number().optional(),
  })
  .nullable()
  .optional();

const ExtractionHintsSchema = z
  .object({
    aiPromptHint: z.string(),
    exampleValues: z.array(z.string()).optional(),
    synonyms: z.array(z.string()).optional(),
  })
  .nullable()
  .optional();

export const FieldUIConfigSchema = z
  .object({
    widget: z
      .enum([
        "text",
        "textarea",
        "select",
        "date-picker",
        "number",
        "toggle",
        "currency",
      ])
      .optional(),
    placeholder: z.string().optional(),
    width: z.enum(["sm", "md", "lg", "full"]).optional(),
    section: z.string().optional(),
  })
  .nullable()
  .optional();

const CompiledExtractionSchemaSummarySchema = z.object({
  schemaDefinitionId: UUIDSchema,
  schemaDefinitionVersion: z.number(),
  schemaDefinitionName: z.string(),
  schemaDefinitionSlug: z.string(),
  provider: z.literal("extend_ai"),
});

const ExtractedDocumentSchema = z.object({
  documentId: UUIDSchema,
  extractionSchemaId: UUIDSchema,
  extractionSchemaVersion: z.number(),
  extractionSchemaName: z.string(),
  extractionSchemaSlug: z.string(),
  provider: z.literal("extend_ai"),
  status: z.literal("completed"),
  extractedPayload: z.record(z.string(), z.unknown()).nullable(),
  processingMetadata: z.record(z.string(), z.unknown()).nullable(),
});

// ============================================================================
// Response Schemas
// ============================================================================

// Recursive field definition schema using z.lazy
const BaseSchemaFieldDefinitionResponseSchema = z.object({
  id: UUIDSchema,
  fieldName: z.string(),
  fieldType: SchemaFieldTypeSchema,
  label: z.string(),
  description: z.string().nullable(),
  isRequired: z.boolean(),
  isMultiple: z.boolean(),
  sortOrder: z.number(),
  coreEnumId: UUIDSchema.nullable(),
  enumValues: z.array(z.string()).nullable().optional(),
  parentFieldId: UUIDSchema.nullable().optional(),
  defaultValue: z.unknown().nullable(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
  indexes: FieldIndexesSchema,
  referencedEntityType: ReferencedEntityTypeSchema,
});

type BaseSchemaFieldDefinitionResponse = z.infer<
  typeof BaseSchemaFieldDefinitionResponseSchema
>;

export type SchemaFieldDefinitionResponse =
  BaseSchemaFieldDefinitionResponse & {
    children?: SchemaFieldDefinitionResponse[];
  };

export const SchemaFieldDefinitionResponseSchema: z.ZodType<SchemaFieldDefinitionResponse> =
  BaseSchemaFieldDefinitionResponseSchema.extend({
    children: z
      .lazy(() => z.array(SchemaFieldDefinitionResponseSchema))
      .optional(),
  });

// Schema types that carry an extraction `mode`; `sql` does not.
const ExtractionSchemaTypeSchema = z.enum(["extraction"]);

// Keep shared metadata alongside the existing response variants.
const SchemaDescriptionResponseSchema = z.object({
  description: z.string().nullable().optional(),
});

const BaseSchemaDefinitionSnapshotSchema = z.object({
  name: z.string(),
  slug: z.string(),
  targetModel: z.string(),
  baseSchemaRef: z.string().nullable().optional(),
  // Free-form on the wire so the frontend can render orphan / deprecated
  // values from older versions without rejecting the response. The strict
  // enum lives at write-time in the backend service.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
  fields: z.array(SchemaFieldDefinitionResponseSchema),
});

// Read model: `sql` snapshots have no `mode`; extraction snapshots carry it.
const SchemaDefinitionSnapshotSchema = z.intersection(
  z.union([
    BaseSchemaDefinitionSnapshotSchema.extend({ schemaType: z.literal("sql") }),
    BaseSchemaDefinitionSnapshotSchema.extend({
      schemaType: ExtractionSchemaTypeSchema,
      mode: SchemaModeSchema,
    }),
  ]),
  SchemaDescriptionResponseSchema,
);

export const SchemaDefinitionVersionResponseSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  version: z.number(),
  snapshot: SchemaDefinitionSnapshotSchema,
  createdBy: UUIDSchema.nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const BaseSchemaDefinitionResponseSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  slug: z.string(),
  targetModel: z.string(),
  baseSchemaRef: z.string().nullable(),
  activeVersionId: UUIDSchema.nullable().optional(),
  version: z.number(),
  isActive: z.boolean(),
  // Tolerated as an arbitrary string so deprecated/unknown values from old
  // rows still round-trip cleanly to the editor's "Deprecated" badge.
  // Write paths (create/update) pin this to the `DocumentType` enum.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable(),
  // Additional column tuples (top-level field ids) that uniquely identify a
  // row beyond `keyedBy` — user-declared or discovered from data. Advisory:
  // no physical UNIQUE constraint is provisioned.
  uniqueIndexes: z.array(z.array(z.string().uuid())).nullable(),
  // Subset of unique indexes enforced as real UNIQUE indexes on the stateful
  // table's physical storage. Read-only here — managed by the enforcement
  // flow, not schemaDefinitions.update.
  enforcedUniqueIndexes: z.array(z.array(z.string().uuid())).nullable(),
  createdByUserId: UUIDSchema.nullable().optional(),
  ownedByUserId: UUIDSchema.nullable().optional(),
  fields: z.array(SchemaFieldDefinitionResponseSchema),
  createdAt: z.date(),
  updatedAt: z.date(),
});

// Read model: narrow on `schemaType`. `sql` schemas have no `mode`;
// extraction schemas always carry one.
export const SchemaDefinitionResponseSchema = z.intersection(
  z.union([
    BaseSchemaDefinitionResponseSchema.extend({ schemaType: z.literal("sql") }),
    BaseSchemaDefinitionResponseSchema.extend({
      schemaType: ExtractionSchemaTypeSchema,
      mode: SchemaModeSchema,
    }),
  ]),
  SchemaDescriptionResponseSchema,
);

export const ListSchemaDefinitionsInputSchema = z.object({
  targetModel: z.string().optional().describe("Only schemas for this model"),
  schemaType: SchemaTypeSchema.optional().describe("Only this schema type"),
  isActive: z
    .boolean()
    .optional()
    .describe("Only active (or inactive) schemas"),
  // Pass `null` to find schemas with no documentType set (untargeted).
  // Pass a `DocumentType` string to find schemas wired to that classification.
  // Omit to skip the filter entirely.
  documentType: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Document type to match; null for schemas with none; omit for no filter",
    ),
});

export const GetSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
});

export const CreateSchemaDefinitionInputSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1),
  targetModel: z.string().min(1),
  schemaType: SchemaTypeSchema,
  // Extraction schemas set this (standalone/extend_base); `sql` schemas omit it.
  mode: SchemaModeSchema.optional(),
  baseSchemaRef: z.string().nullable().optional(),
  // Forwarded to the backend service which validates against the
  // `DocumentType` enum and rejects unknowns. Wire-side stays a string so
  // callers don't have to mirror the enum.
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
});

export const UpdateSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  slug: z.string().min(1).optional(),
  mode: SchemaModeSchema.optional(),
  baseSchemaRef: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  documentType: z.string().nullable().optional(),
  keyedBy: z.array(z.string().uuid()).nullable().optional(),
  // Tuples reference existing top-level field ids, so this is update-only:
  // fields don't exist yet when a definition is created.
  uniqueIndexes: z
    .array(z.array(z.string().uuid()).min(1))
    .nullable()
    .optional(),
});

export const DeleteSchemaDefinitionInputSchema = z.object({
  id: UUIDSchema,
});

export const AddFieldInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  parentFieldId: UUIDSchema.nullable().optional(),
  fieldName: z.string().min(1),
  fieldType: SchemaFieldTypeSchema,
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  isMultiple: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative(),
  coreEnumId: UUIDSchema.nullable().optional(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
  indexes: FieldIndexesSchema,
  referencedEntityType: ReferencedEntityTypeSchema,
});

export const UpdateFieldInputSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  fieldName: z.string().min(1).optional(),
  fieldType: SchemaFieldTypeSchema.optional(),
  label: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  isMultiple: z.boolean().optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  coreEnumId: UUIDSchema.nullable().optional(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  uiConfig: FieldUIConfigSchema,
  indexes: FieldIndexesSchema,
  referencedEntityType: ReferencedEntityTypeSchema,
});

export const RemoveFieldInputSchema = z.object({
  id: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
});

export const ReorderFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  parentFieldId: UUIDSchema.nullable().optional(),
  fieldIds: z.array(UUIDSchema),
});

// Recursive node describing a single field to import (with optional children).
export const BaseImportFieldNodeSchema = z.object({
  fieldName: z.string().min(1),
  fieldType: SchemaFieldTypeSchema,
  label: z.string().min(1),
  description: z.string().nullable().optional(),
  isRequired: z.boolean(),
  enumValues: z.array(z.string()).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  validationRules: ValidationRulesSchema,
  extractionHints: ExtractionHintsSchema,
  indexes: FieldIndexesSchema,
  referencedEntityType: ReferencedEntityTypeSchema,
});

type BaseImportFieldNode = z.infer<typeof BaseImportFieldNodeSchema>;

export type ImportFieldNode = BaseImportFieldNode & {
  children?: ImportFieldNode[];
};

export const ImportFieldNodeSchema: z.ZodType<ImportFieldNode> =
  BaseImportFieldNodeSchema.extend({
    children: z.lazy(() => z.array(ImportFieldNodeSchema)).optional(),
  });

export const ImportFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  fields: z.array(ImportFieldNodeSchema).min(1),
});

export const ImportFieldsResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  schema: SchemaDefinitionResponseSchema,
});

export const PreviewJsonSchemaInputSchema = z.object({
  id: UUIDSchema,
});

export const RunExtractionPreviewInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  documentId: UUIDSchema,
});

// Version request schemas
export const PublishVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  /**
   * Required only when the schema is a registered entity extension AND
   * publishing would drop or retype a column: the user re-enters the extension's
   * name to confirm. Verified server-side against the recomputed diff — see
   * `core.schemaExtensions.previewPublishSync` for what the prompt should show.
   */
  extensionSyncConfirmation: z.string().optional(),
});

export const ListVersionsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
});

export const GetVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema.describe("Schema definition id"),
  versionId: UUIDSchema.describe("Version id to fetch"),
});

export const ActivateVersionInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  versionId: UUIDSchema,
});

export const RunExtractionPreviewResponseSchema = z.object({
  compiledExtractionSchema: CompiledExtractionSchemaSummarySchema,
  extractedDocument: ExtractedDocumentSchema,
  warnings: z.array(z.string()),
});

export const AssistSchemaImportFieldsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  instruction: z.string().min(1).max(16_000),
});

export const AssistEditSchemaResponseSchema = z.object({
  fields: z.array(ImportFieldNodeSchema),
  explanation: z.string(),
});

/** @deprecated Use AssistEditSchemaResponseSchema — assist no longer persists directly. */
export const AssistSchemaImportFieldsResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  schema: SchemaDefinitionResponseSchema,
  explanation: z.string(),
});

export const SyncFieldsFromImportTreeInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  fields: z.array(ImportFieldNodeSchema),
});

export const SyncFieldsFromImportTreeResponseSchema = z.object({
  insertedCount: z.number().int().nonnegative(),
  updatedCount: z.number().int().nonnegative(),
  deletedCount: z.number().int().nonnegative(),
});

export const PreviewAssistImportFieldsInputSchema = z.object({
  instruction: z.string().min(1).max(16_000),
  existingRootFieldNames: z.array(z.string()).optional().default([]),
});

export const PreviewAssistImportFieldsResponseSchema = z.object({
  fields: z.array(ImportFieldNodeSchema).min(1),
  explanation: z.string(),
});

export const GetSchemaLoadTransformInputSchema = z.object({
  schemaId: UUIDSchema,
});

export const UpdateSchemaLoadTransformInputSchema = z.object({
  schemaId: UUIDSchema,
  /** null clears the transform; a direct load then refuses the schema's links. */
  loadTransform: loadTransformSchema.nullable(),
});

export const LoadTransformStepOptionSchema = z.object({
  stepType: z.string(),
  label: z.string(),
  inputSockets: z.array(
    z.object({ id: z.string(), label: z.string(), required: z.boolean() }),
  ),
});

export const SchemaLoadTransformResponseSchema = z.object({
  loadTransform: loadTransformSchema.nullable(),
  /** The schema's ingest columns (root field names) a transform can read. */
  columns: z.array(z.string()),
  /** Steps a load transform may apply, with their input sockets. */
  stepOptions: z.array(LoadTransformStepOptionSchema),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const schemaDefinitionsContracts = {
  labelDrafts: labelDraftContract,
  list: oc
    .input(ListSchemaDefinitionsInputSchema)
    .output(z.array(SchemaDefinitionResponseSchema))
    .route({
      summary: "List schema definitions",
      description:
        "Returns the organization's schema definitions with their field trees, filterable by target model, schema type, active flag, and document type (null for untargeted).",
      method: "GET",
    }),

  get: oc
    .input(GetSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({
      summary: "Get a schema definition",
      description:
        "Returns one schema definition by id with its full nested field tree, keys, unique indexes, document type, and active version.",
      method: "GET",
    }),

  create: oc
    .input(CreateSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({
      summary: "Create a schema definition",
      description:
        "Creates an extraction or SQL schema for a target model with name, slug, mode, document type, and key fields. The caller becomes creator and owner.",
      method: "POST",
    }),

  update: oc
    .input(UpdateSchemaDefinitionInputSchema)
    .output(SchemaDefinitionResponseSchema)
    .route({
      summary: "Update a schema definition",
      description:
        "Updates a schema's name, slug, mode, base schema, active flag, document type, key fields, or unique indexes. Returns the updated schema.",
      method: "PATCH",
    }),

  delete: oc
    .input(DeleteSchemaDefinitionInputSchema)
    .output(z.object({ success: z.boolean(), id: UUIDSchema }))
    .route({
      summary: "Delete a schema definition",
      description:
        "Deletes a schema definition. Refused while it backs a stateful table or is still referenced elsewhere.",
      method: "DELETE",
    }),

  addField: oc
    .input(AddFieldInputSchema)
    .output(SchemaFieldDefinitionResponseSchema)
    .route({
      summary: "Add a field to a schema",
      description:
        "Adds one field (optionally nested under a parent) with type, label, validation, extraction hints, enum values, indexes, and entity reference. Returns the new field.",
      method: "POST",
    }),

  updateField: oc
    .input(UpdateFieldInputSchema)
    .output(SchemaFieldDefinitionResponseSchema)
    .route({
      summary: "Update a schema field",
      description:
        "Changes a schema field's name, type, label, requiredness, validation, extraction hints, UI config, indexes, or other settings. Returns the updated field.",
      method: "PATCH",
    }),

  removeField: oc
    .input(RemoveFieldInputSchema)
    .output(z.object({ success: z.boolean(), id: UUIDSchema }))
    .route({
      summary: "Remove a field from a schema",
      description: "Deletes one field from a schema definition.",
      method: "DELETE",
    }),

  reorderFields: oc
    .input(ReorderFieldsInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Reorder schema fields",
      description:
        "Sets the display order of a schema's fields under one parent (or the top level) from an ordered list of field ids.",
      method: "POST",
    }),

  importFields: oc
    .input(ImportFieldsInputSchema)
    .output(ImportFieldsResponseSchema)
    .route({
      summary: "Bulk import fields into a schema",
      description:
        "Inserts a pasted tree of fields, including nested children, into a schema in one transaction. Returns the inserted count and the reloaded schema.",
      method: "POST",
    }),

  assistEditSchema: oc
    .input(AssistSchemaImportFieldsInputSchema)
    .output(AssistEditSchemaResponseSchema)
    .route({
      summary: "Draft schema field edits with AI",
      description:
        "Generates a proposed full field tree for a schema from a natural-language instruction, with an explanation. Nothing is saved; apply it separately.",
      method: "POST",
    }),

  /** @deprecated Use assistEditSchema */
  assistImportFields: oc
    .input(AssistSchemaImportFieldsInputSchema)
    .output(AssistSchemaImportFieldsResponseSchema)
    .route({
      summary: "Add schema fields with AI",
      description:
        "Deprecated. Generates new fields from a natural-language instruction and inserts them into the schema immediately. Returns the inserted count, explanation, and reloaded schema.",
      method: "POST",
    }),

  syncFieldsFromImportTree: oc
    .input(SyncFieldsFromImportTreeInputSchema)
    .output(SyncFieldsFromImportTreeResponseSchema)
    .route({
      summary: "Apply a field tree to a schema",
      description:
        "Replaces a schema's fields to match a supplied field tree, inserting, updating, and deleting fields in one transaction. Returns the inserted, updated, and deleted counts.",
      method: "POST",
    }),

  previewAssistImportFields: oc
    .input(PreviewAssistImportFieldsInputSchema)
    .output(PreviewAssistImportFieldsResponseSchema)
    .route({
      summary: "Preview AI-generated schema fields",
      description:
        "Generates proposed new fields from a natural-language instruction, avoiding given existing top-level names. Returns fields and an explanation; nothing is saved.",
      method: "POST",
    }),

  previewJsonSchema: oc
    .input(PreviewJsonSchemaInputSchema)
    .output(z.object({ jsonSchema: z.record(z.string(), z.unknown()) }))
    .route({
      summary: "Preview a schema as JSON Schema",
      description:
        "Returns the JSON Schema the extraction step would use for a schema definition's current fields, mode, and base schema.",
      method: "GET",
    }),

  runExtractionPreview: oc
    .input(RunExtractionPreviewInputSchema)
    .output(RunExtractionPreviewResponseSchema)
    .route({
      summary: "Test an extraction schema on a document",
      description:
        "Runs an extraction schema's current fields against one of your uploaded documents and returns the extracted data. Only extraction schemas are allowed.",
      method: "POST",
    }),

  publishVersion: oc
    .input(PublishVersionInputSchema)
    .output(SchemaDefinitionVersionResponseSchema)
    .route({
      summary: "Publish a schema version",
      description:
        "Snapshots the schema's current fields as a new version. For entity extensions it also alters the extension table; dropping or retyping columns requires typing the extension name.",
      method: "POST",
    }),

  listVersions: oc
    .input(ListVersionsInputSchema)
    .output(z.array(SchemaDefinitionVersionResponseSchema))
    .route({
      summary: "List schema versions",
      description:
        "Returns every published version of a schema definition, each with its field snapshot, author, and timestamps.",
      method: "GET",
    }),

  getVersion: oc
    .input(GetVersionInputSchema)
    .output(SchemaDefinitionVersionResponseSchema)
    .route({
      summary: "Get a schema version",
      description:
        "Returns one published version of a schema definition with its full field snapshot.",
      method: "GET",
    }),

  activateVersion: oc
    .input(ActivateVersionInputSchema)
    .output(z.object({ success: z.boolean(), activeVersionId: UUIDSchema }))
    .route({
      summary: "Activate a schema version",
      description:
        "Makes a previously published version the schema's active version. Refused for schemas backing a stateful table.",
      method: "POST",
    }),

  getSchemaLoadTransform: oc
    .input(GetSchemaLoadTransformInputSchema)
    .output(SchemaLoadTransformResponseSchema)
    .route({
      summary: "Get a schema's load transform",
      description:
        "Returns the step and input wiring a direct data-source-link load applies to this schema's ingest rows, with the schema's ingest columns and the sink steps (and their input sockets) a transform may use.",
      method: "GET",
    }),

  updateSchemaLoadTransform: oc
    .input(UpdateSchemaLoadTransformInputSchema)
    .output(SchemaLoadTransformResponseSchema)
    .route({
      summary: "Set a schema's load transform",
      description:
        "Sets or clears the step a direct load applies to this schema's ingest rows and which ingest column or reserved source feeds each step input. Rejects an unknown step, an input the step lacks, or a source that is neither a reserved source nor a schema column.",
      method: "PUT",
    }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type SchemaDefinitionResponse = z.infer<
  typeof SchemaDefinitionResponseSchema
>;
export type SchemaDefinitionVersionResponse = z.infer<
  typeof SchemaDefinitionVersionResponseSchema
>;
export type ListSchemaDefinitionsInput = z.infer<
  typeof ListSchemaDefinitionsInputSchema
>;
export type CreateSchemaDefinitionInput = z.infer<
  typeof CreateSchemaDefinitionInputSchema
>;
export type UpdateSchemaDefinitionInput = z.infer<
  typeof UpdateSchemaDefinitionInputSchema
>;
export type AddFieldInput = z.infer<typeof AddFieldInputSchema>;
export type UpdateFieldInput = z.infer<typeof UpdateFieldInputSchema>;
export type ImportFieldsInput = z.infer<typeof ImportFieldsInputSchema>;
export type ImportFieldsResponse = z.infer<typeof ImportFieldsResponseSchema>;
export type AssistSchemaImportFieldsInput = z.infer<
  typeof AssistSchemaImportFieldsInputSchema
>;
export type AssistEditSchemaResponse = z.infer<
  typeof AssistEditSchemaResponseSchema
>;
export type SyncFieldsFromImportTreeInput = z.infer<
  typeof SyncFieldsFromImportTreeInputSchema
>;
export type SyncFieldsFromImportTreeResponse = z.infer<
  typeof SyncFieldsFromImportTreeResponseSchema
>;
export type AssistSchemaImportFieldsResponse = z.infer<
  typeof AssistSchemaImportFieldsResponseSchema
>;
export type PreviewAssistImportFieldsInput = z.infer<
  typeof PreviewAssistImportFieldsInputSchema
>;
export type PreviewAssistImportFieldsResponse = z.infer<
  typeof PreviewAssistImportFieldsResponseSchema
>;
export type RunExtractionPreviewInput = z.infer<
  typeof RunExtractionPreviewInputSchema
>;
export type RunExtractionPreviewResponse = z.infer<
  typeof RunExtractionPreviewResponseSchema
>;
export type SchemaLoadTransformResponse = z.infer<
  typeof SchemaLoadTransformResponseSchema
>;
export type LoadTransformStepOption = z.infer<
  typeof LoadTransformStepOptionSchema
>;
export type UpdateSchemaLoadTransformInput = z.infer<
  typeof UpdateSchemaLoadTransformInputSchema
>;
